import { createContext, useContext, type CSSProperties, type ReactNode } from 'react';
import '../sampling/sampleRegistration/sampleRegistration.css';

/**
 * "Sample Registration look" for the Maintenance full-page details (owner request 2026-10-22: the whole site must look identical
 * and seamless — the client page takes the Sample Registration form's section cards, label : field grid, tab strip and fonts).
 *
 * It is the SAME stylesheet and class names Sample Registration uses (`sr-sec` / `sr-sec-h` / `sr-sec-b` / `sr-fgrid` / `sr-tabbar`),
 * not a copy, so the two forms cannot drift. The editors (client / contact / project) are also shown as popups elsewhere, so the
 * layout is switched by this context: the Maintenance page provides `true`; every other caller keeps the popup layout untouched.
 */
export const SrLayout = createContext(false);
export const useSrLayout = (): boolean => useContext(SrLayout);

/** True inside the client page's tab grids: ONE edit pencil per row that opens the record (owner 2026-10-22, as the Sample Registration and
 *  Utility grids) instead of G5's pair (edit in place + open). Separate from SrLayout so it never reaches the field helpers. */
export const SrSingleEdit = createContext(false);
export const useSrSingleEdit = (): boolean => useContext(SrSingleEdit);

/** A Sample-Registration group box: a header band (icon + title) over the body. */
export function SrCard({ title, icon, children }: { title?: string; icon?: string; children: ReactNode }) {
  return (
    <section className="sr-sec">
      {title && <h3 className="sr-sec-h">{icon && <span className="sr-sec-ico">{icon}</span>} {title}</h3>}
      <div className="sr-sec-b">{children}</div>
    </section>
  );
}

/** The editor body: a Sample-Registration card on the Maintenance page, the original padded scroll area in the popups. */
export function SrBody({ page, title, icon, popupStyle, children }: {
  page: boolean; title: string; icon: string; popupStyle: CSSProperties; children: ReactNode;
}) {
  if (!page) return <div style={popupStyle}>{children}</div>;
  return <SrCard title={title} icon={icon}>{children}</SrCard>;
}

/** Two-column label : field grid (one per column of a two-column form, e.g. the Contact and Project pages). */
export function SrColumns({ children }: { children: ReactNode }) {
  return <div className="maint-cols">{children}</div>;
}
